(ns io.daskul.webapp.widgets.form.collection
  "Collection widget for editing vector-of-maps JSONB fields.

   Renders accordion-style: collapsed rows show a summary line,
   expanded row shows form inputs. Only one row open at a time.

   Schema-driven: parses [:vector [:map ...]] to discover sub-fields,
   renders appropriate inputs (text, number, select for enums)."
  (:require
   [clojure.string :as str]
   [uix.core :as uix :refer [defui $]]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.display :as display]))

;; ============================================================================
;; Schema Parsing
;; ============================================================================

(defn- unwrap-maybe [schema]
  (if (and (vector? schema) (= "maybe" (first schema)))
    (second schema)
    schema))

(defn- parse-sub-field
  "Parse a single [:map ...] entry into a field descriptor."
  [entry]
  (let [entry (vec entry)
        field-name (nth entry 0)
        has-opts? (map? (nth entry 1 nil))
        opts (if has-opts? (nth entry 1) {})
        schema (if has-opts? (nth entry 2) (nth entry 1))
        unwrapped (unwrap-maybe schema)
        optional? (or (:optional opts) (get opts "optional") false)
        field-type (cond
                     (and (sequential? unwrapped) (= "enum" (first unwrapped)))
                     :enum
                     (= unwrapped "int") :int
                     :else :string)]
    (cond-> {:key (keyword field-name)
             :label (display/humanize-enum-value field-name)
             :type field-type
             :required (not optional?)}
      (= field-type :enum)
      (assoc :options (mapv (fn [v] {:value v :label (display/humanize-enum-value v)})
                            (cljs.core/rest unwrapped))))))

(defn parse-collection-schema
  "Extract sub-field descriptors from [:maybe [:vector [:map ...]]] schema."
  [schema]
  (let [inner (-> schema unwrap-maybe)]
    (when (and (vector? inner) (= "vector" (first inner)))
      (let [map-schema (second inner)]
        (when (and (vector? map-schema) (= "map" (first map-schema)))
          (mapv parse-sub-field (rest map-schema)))))))

;; ============================================================================
;; Helpers
;; ============================================================================

(defn- row-summary
  "Build collapsed summary from row data and sub-fields."
  [row sub-fields]
  (let [parts (->> sub-fields
                   (map (fn [{:keys [key type]}]
                          (let [v (get row key)]
                            (when (and v (not= v ""))
                              (if (= type :enum)
                                (display/humanize-enum-value v)
                                (str v))))))
                   (remove nil?))]
    (if (seq parts)
      (str/join " · " parts)
      "New entry")))

;; ============================================================================
;; Components
;; ============================================================================

(defui collection-row
  "Expanded row: renders form inputs for each sub-field."
  [{:keys [row sub-fields on-update on-remove]}]
  ($ ui/stack {:gap "sm"}
    ($ ui/simple-grid {:cols #js {:base 1 :sm 2} :spacing "sm"}
      (for [{:keys [key label type required options]} sub-fields]
        (case type
          :enum
          ($ ui/select
            {:key (name key)
             :label label
             :data (clj->js options)
             :value (get row key)
             :onChange #(on-update key %)
             :required required
             :clearable (not required)
             :searchable true})

          :int
          ($ ui/text-input
            {:key (name key)
             :label label
             :type "number"
             :value (or (some-> (get row key) str) "")
             :onChange #(let [v (.. % -target -value)]
                          (on-update key (when (seq v) (js/parseInt v 10))))
             :required required})

          ;; default: string
          ($ ui/text-input
            {:key (name key)
             :label label
             :value (or (get row key) "")
             :onChange #(on-update key (.. % -target -value))
             :required required}))))
    ($ ui/group {:justify "flex-end" :mt "xs"}
      ($ ui/button {:size "compact-xs" :variant "subtle" :color "red"
                    :leftSection ($ icons/icon-trash {:size 14})
                    :onClick on-remove}
        "Remove"))))

(defui collection-widget
  "Accordion-style collection editor for vector-of-maps fields.

   Props:
   - value: vector of maps — current value
   - set-field!: (fn [new-vec]) — updates parent form
   - schema: vector — Malli schema to parse sub-fields
   - ui: map — {:label ...}"
  [{:keys [value set-field! schema ui]}]
  (let [items (or value [])
        sub-fields (uix/use-memo #(parse-collection-schema schema) [schema])
        label (:label ui)
        [expanded set-expanded!] (uix/use-state nil)

        update-item! (fn [idx key val]
                       (set-field! (assoc-in (vec items) [idx key] val)))
        remove-item! (fn [idx]
                       (let [new-items (vec (concat (subvec (vec items) 0 idx)
                                                    (subvec (vec items) (inc idx))))]
                         (set-field! new-items)
                         (set-expanded! nil)))
        add-item! (fn []
                    (let [new-idx (count items)
                          empty-row (zipmap (map :key sub-fields) (repeat nil))]
                      (set-field! (conj (vec items) empty-row))
                      (set-expanded! (str new-idx))))]

    ($ ui/stack {:gap "xs"}
      (when label
        ($ ui/text {:fw 600 :size "sm"} label))

      (when (seq items)
        ($ ui/accordion {:value expanded
                         :onChange set-expanded!
                         :variant "separated"}
          (for [[idx item] (map-indexed vector items)]
            (let [key-str (str idx)]
              ($ ui/accordion-item {:key key-str :value key-str}
                ($ ui/accordion-control
                  ($ ui/text {:size "sm"}
                    (row-summary item sub-fields)))
                ($ ui/accordion-panel
                  ($ collection-row
                    {:row item
                     :sub-fields sub-fields
                     :on-update (fn [k v] (update-item! idx k v))
                     :on-remove #(remove-item! idx)})))))))

      ($ ui/button {:size "xs" :variant "default" :onClick add-item!}
        (str "+ Add " (or label "Item"))))))
